export const CLAUDE_HANDOFF_MAX_PROMPT = 12_000;
/** Shown instead of the organization notices while the person's own pause is what keeps a feature off. */
export const LOCAL_FILES_PAUSED_NOTICE = 'Local file tasks are off while Pause autonomy is on. Turn it off in the Gyro tray menu to use them.';
export const CLAUDE_HANDOFF_PAUSED_NOTICE = 'Opening a draft in Claude is off while Pause autonomy is on. Turn it off in the Gyro tray menu to use it.';
const metadata = (value, limit) => typeof value === 'string'
    && value.trim().length > 0 && value.length <= limit && !/[\u0000-\u001f\u007f]/.test(value);
/** Display-only native metadata; never use these paths to authorize a file operation. */
export function parseLocalFilesSnapshot(value) {
    if (!value || typeof value !== 'object')
        return null;
    const item = value;
    if (item.available !== true || typeof item.enabled !== 'boolean' || !Array.isArray(item.roots) || item.roots.length > 32)
        return null;
    if (item.claudeHandoff !== undefined && typeof item.claudeHandoff !== 'boolean')
        return null;
    if ((item.paused !== undefined && typeof item.paused !== 'boolean')
        || (item.claudeHandoffPaused !== undefined && typeof item.claudeHandoffPaused !== 'boolean'))
        return null;
    if (item.error != null && !metadata(item.error, 500))
        return null;
    const roots = [];
    const ids = new Set();
    for (const root of item.roots) {
        if (!root || typeof root !== 'object' || !metadata(root.id, 200) || !metadata(root.name, 260)
            || !metadata(root.path, 4096) || ids.has(root.id))
            return null;
        ids.add(root.id);
        roots.push({ id: root.id, name: root.name, path: root.path });
    }
    // An older native build that does not report the handoff switch is one where the handoff was never offered by policy.
    // The pause flags only ever explain a feature that is off; an older native build sends neither.
    const claudeHandoff = item.claudeHandoff === true;
    return {
        available: true, enabled: item.enabled, claudeHandoff, roots,
        ...(item.paused === true && !item.enabled ? { paused: true } : {}),
        ...(item.claudeHandoffPaused === true && !claudeHandoff ? { claudeHandoffPaused: true } : {}),
        ...(item.error ? { error: item.error } : {}),
    };
}
export function parseClaudeHandoffStatus(value) {
    if (!value || typeof value !== 'object')
        return null;
    const status = value.status;
    return status === 'opened' || status === 'cancelled' || status === 'unavailable' || status === 'error' || status === 'too_long' || status === 'disabled' ? status : null;
}
/** `paused`: the latest native snapshot says the handoff is off only because of the person's own Pause autonomy. */
export function claudeHandoffMessage(status, paused = false) {
    if (status === 'disabled' && paused)
        return 'Opening a draft in Claude is off while Pause autonomy is on. Turn it off in the Gyro tray menu, then try again. Your draft has been kept.';
    switch (status) {
        case 'opened': return 'Opened in Claude — review and send there. Your Gyro draft is still here.';
        case 'cancelled': return 'Handoff cancelled. Your Gyro draft is still here.';
        case 'unavailable': return 'Claude Desktop is not available on this PC. Your draft has been kept.';
        case 'error': return 'Could not open Claude. Your draft has been kept; try again.';
        case 'too_long': return 'This draft is too long to open in Claude once encoded. Shorten it and try again; your draft has been kept.';
        case 'disabled': return 'Opening a draft in Claude is turned off by your organization. Your draft has been kept.';
    }
}
export function canHandoffToClaude(prompt, disabled = false) {
    return !disabled && prompt.trim().length > 0 && prompt.length <= CLAUDE_HANDOFF_MAX_PROMPT
        && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(prompt);
}
/** The local actions (PR #367) whose cards must name every folder: their ids are opaque, so the person reads the name. */
const FOLDER_NAMED_TOOLS = new Set(['list_folder', 'rename_items', 'move_items', 'copy_items', 'delete_items', 'write_file', 'open_item']);
/** gyro-local-commands' one tool (desktop LocalCommandService): a script that runs in a connected folder or in Documents. */
const RUN_POWERSHELL = 'run_powershell';
/**
 * The folders a gyro-local-files action or a gyro-local-commands run_powershell names, each resolved against the latest
 * native snapshot (display only: the PC checks every id itself), so the approval card shows "Project A" and its path
 * instead of a 32-hex id. Null for any other action. An id the snapshot does not list comes back with `root: null`.
 */
export function localActionFolders(action, snapshot) {
    if (action.tool !== 'mcp_call' || !action.input || typeof action.input !== 'object')
        return null;
    const { server, tool, args } = action.input;
    if (typeof server !== 'string' || !args || typeof args !== 'object')
        return null;
    const serverName = server.trim().toLowerCase();
    const resolve = (id) => snapshot?.roots.find((root) => root.id === id) ?? null;
    const { rootId, items } = args;
    // A script runs in the connected folder its rootId names, or in Documents when it names none; the PC refuses any other
    // rootId (a value that is not a string included), so that one is a folder that is not connected.
    if (serverName === 'gyro-local-commands' && tool === RUN_POWERSHELL) {
        if (!('rootId' in args))
            return [{ role: 'runs', id: '', root: null, documents: true }];
        return [typeof rootId === 'string' ? { role: 'runs', id: rootId, root: resolve(rootId) } : { role: 'runs', id: '', root: null }];
    }
    if (serverName !== 'gyro-local-files')
        return null;
    const folders = [];
    if (typeof rootId === 'string')
        folders.push({ role: 'from', id: rootId, root: resolve(rootId) });
    const seen = new Set(typeof rootId === 'string' ? [rootId] : []);
    for (const item of Array.isArray(items) ? items : []) {
        const to = item && typeof item === 'object' ? item.toRootId : undefined;
        if (typeof to !== 'string' || seen.has(to))
            continue;
        seen.add(to);
        folders.push({ role: 'to', id: to, root: resolve(to) });
    }
    return folders;
}
/**
 * Whether a local action may be confirmed as far as its folders go: a local action of PR #367 and run_powershell need every
 * folder they name to be one the person has connected now (the card names each one; a script without a rootId runs in
 * Documents). Search, read and create keep their behaviour, and any other action is unaffected.
 */
export function localActionFoldersConfirmable(action, snapshot) {
    const folders = localActionFolders(action, snapshot);
    if (!folders)
        return true;
    const tool = action.input.tool;
    return typeof tool !== 'string' || !(FOLDER_NAMED_TOOLS.has(tool) || tool === RUN_POWERSHELL)
        || (folders.length > 0 && folders.every((folder) => folder.documents === true || folder.root !== null));
}
//# sourceMappingURL=localFiles.js.map